import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  QrCode,
  AlertTriangle,
  Package,
  MapPin,
  ShieldAlert,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  getAllAssetMovements,
  scanAssetTag,
  approveAssetMovement,
  reportAssetTamper,
} from "../../../service/securityService";
import {
  AssetMovement as AssetMovementType,
  SecuritySearchFilters,
  PaginationParams,
} from "../../../types/security_types";
import axiosInstance from "../../../common/axiosInstance";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

function securityEventLabel(
  t: string | undefined,
  row?: AssetMovementType,
): string {
  if (!t && row?.movementType === "Unauthorized") {
    return "Unauthorized movement";
  }
  switch (t) {
    case "UnauthorizedMovement":
      return "Unauthorized movement";
    case "AssetLeftPremise":
      return "Left premise";
    case "TamperDetected":
      return "Tamper";
    case "AuthorizedMovement":
      return "Normal";
    default:
      return "—";
  }
}

const AssetMovementTracking: React.FC = () => {
  const [movements, setMovements] = useState<AssetMovementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("ALL");
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isTamperModalOpen, setIsTamperModalOpen] = useState(false);
  const [tamperTag, setTamperTag] = useState("");
  const [tamperAssetId, setTamperAssetId] = useState("");
  const [tamperZone, setTamperZone] = useState("");
  const [tamperLocation, setTamperLocation] = useState("");
  const [tamperNotes, setTamperNotes] = useState("");
  const [scanTag, setScanTag] = useState("");
  const [scanZone, setScanZone] = useState("");
  const [scanLocation, setScanLocation] = useState("");
  const [zones, setZones] = useState<Array<{ value: string; label: string }>>(
    [],
  );
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 20,
  });

  const handleApproveMovement = async (movementId: string) => {
    try {
      await approveAssetMovement(movementId);
      toast.success("Movement approved successfully");
      fetchMovements();
    } catch (error: any) {
      console.error("Error approving movement:", error);
      toast.error(
        error.response?.data?.message || "Failed to approve asset movement",
      );
    }
  };

  useEffect(() => {
    fetchMovements();
    fetchZones();
  }, [
    searchQuery,
    selectedRegion,
    selectedPremises,
    statusFilter,
    eventTypeFilter,
    pagination,
  ]);

  const fetchZones = async () => {
    try {
      const response = await axiosInstance.get("/api/security/access-zones");
      if (response.data.success) {
        const zonesData = response.data.data || response.data.message || [];
        setZones(
          zonesData.map((zone: any) => ({
            value: zone._id || zone.id,
            label: zone.zoneName,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;
      if (statusFilter !== "ALL") filters.status = statusFilter;
      if (eventTypeFilter !== "ALL")
        filters.securityEventType = eventTypeFilter;

      const response = await getAllAssetMovements(filters, pagination);
      setMovements(response.data || []);
    } catch (error: any) {
      console.error("Error fetching asset movements:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to load asset movements",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReportTamper = async () => {
    if (!tamperTag.trim() && !tamperAssetId.trim()) {
      toast.error("Enter equipment tag/ID or equipment record ID");
      return;
    }
    try {
      await reportAssetTamper({
        assetTag: tamperTag.trim() || undefined,
        assetId: tamperAssetId.trim() || undefined,
        zoneId: tamperZone || undefined,
        location: tamperLocation.trim() || undefined,
        notes: tamperNotes.trim() || undefined,
      });
      toast.success("Tamper event logged");
      setIsTamperModalOpen(false);
      setTamperTag("");
      setTamperAssetId("");
      setTamperZone("");
      setTamperLocation("");
      setTamperNotes("");
      fetchMovements();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to report tamper",
      );
    }
  };

  const handleScanTag = async () => {
    if (!scanTag || !scanZone) {
      toast.error("Please provide asset tag and zone");
      return;
    }

    try {
      const result = await scanAssetTag(scanTag, scanZone, scanLocation);
      toast.success(
        result.alert
          ? "Alert: Unauthorized movement detected!"
          : "Asset scanned successfully",
      );
      if (result.alert) {
        toast.warning(`Asset ${result.asset.name} moved to restricted zone`);
      }
      setIsScanModalOpen(false);
      setScanTag("");
      setScanZone("");
      setScanLocation("");
      fetchMovements();
    } catch (error: any) {
      console.error("Error scanning tag:", error);
      toast.error(error.response?.data?.message || "Failed to scan asset tag");
    }
  };

  const getMovementTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      Authorized: "bg-green-100 text-green-800",
      Unauthorized: "bg-red-100 text-red-800",
      Scheduled: "bg-blue-100 text-blue-800",
      Emergency: "bg-orange-100 text-orange-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[type] || "bg-gray-100 text-gray-800"
          }`}
      >
        {type}
      </span>
    );
  };

  const getSecurityEventBadge = (
    t: string | undefined,
    row?: AssetMovementType,
  ) => {
    const label = securityEventLabel(t, row);
    if (label === "—") {
      return <span className="text-xs text-gray-400">—</span>;
    }
    const colors: Record<string, string> = {
      "Unauthorized movement": "bg-red-100 text-red-800",
      "Left premise": "bg-amber-100 text-amber-900",
      Tamper: "bg-purple-100 text-purple-900",
      Normal: "bg-slate-100 text-slate-700",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[label] || "bg-gray-100 text-gray-800"}`}
      >
        {label}
      </span>
    );
  };

  const getAlertLevelBadge = (level: string) => {
    const colors: Record<string, string> = {
      None: "bg-gray-100 text-gray-800",
      Low: "bg-yellow-100 text-yellow-800",
      Medium: "bg-orange-100 text-orange-800",
      High: "bg-red-100 text-red-800",
      Critical: "bg-red-200 text-red-900",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[level] || "bg-gray-100 text-gray-800"
          }`}
      >
        {level}
      </span>
    );
  };

  // Helper function to format location display
  const formatLocationDisplay = (movement: AssetMovementType) => {
    const fromDisplay =
      movement.fromLocation && movement.fromLocation !== "Unknown"
        ? movement.fromLocation
        : movement.fromZoneName || "Unknown";

    const toDisplay =
      movement.toLocation && movement.toLocation !== "Unknown"
        ? movement.toLocation
        : movement.toZoneName || "Unknown";

    return `${fromDisplay} → ${toDisplay}`;
  };

  const gridTheme = themeQuartz.withParams({
    backgroundColor: "#fff",
    foregroundColor: "#2a2a2a",
    headerTextColor: "#2a2a2a",
    headerBackgroundColor: "#E9F5FF",
    oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
    headerColumnResizeHandleColor: "rgb(126, 46, 132)",
    fontSize: "0.75rem",
    headerFontWeight: "bold",
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "assetName",
        headerName: "Asset",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: (params: any) => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          color: params.data?.status === "Alert" ? "#dc2626" : "#111827",
          fontWeight: params.data?.status === "Alert" ? "600" : "500",
        }),
      },
      {
        field: "assetTag",
        headerName: "Tag",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => params.data?.assetTag || "N/A",
      },
      {
        field: "securityEventType",
        headerName: "Security event",
        sortable: true,
        minWidth: 160,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) =>
          getSecurityEventBadge(
            params.data?.securityEventType,
            params.data,
          ),
      },
      {
        field: "movementType",
        headerName: "Movement",
        sortable: true,
        minWidth: 140,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getMovementTypeBadge(params.value || "Authorized");
        },
      },
      {
        field: "fromTo",
        headerName: "From → To",
        sortable: false,
        minWidth: 200,
        flex: 2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const movement = params.data;
          const display = formatLocationDisplay(movement);
          return (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-xs">{display}</span>
            </div>
          );
        },
      },
      {
        field: "detectedBy",
        headerName: "Type",
        sortable: true,
        minWidth: 140,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "alertLevel",
        headerName: "Alert Level",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const level = params.value;
          if (!level || level === "None") return null;
          return getAlertLevelBadge(level);
        },
      },
      {
        field: "detectedAt",
        headerName: "Detected At",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => {
          if (!params.data?.detectedAt) return "";
          return new Date(params.data.detectedAt).toLocaleString();
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 160,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const movement = params.data as AssetMovementType;
          const isApprovable =
            movement &&
            movement.movementType === "Unauthorized" &&
            (movement.status === "Alert" || movement.status === "Pending");

          if (!isApprovable) {
            return (
              <span className="text-xs text-gray-400">
                {movement.status || "—"}
              </span>
            );
          }

          return (
            <button
              type="button"
              onClick={() =>
                handleApproveMovement(movement.id || movement._id!)
              }
              className="px-3 py-1 text-xs rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition"
            >
              Approve
            </button>
          );
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  const theme = useMemo(() => gridTheme, []);

  return (
    <div className="px-2 py-4 w-full h-full">
      <div className="mb-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Package className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Equipment movement tracking
              </h2>
              <p className="text-gray-600 text-sm max-w-2xl">
                Scan RFID/barcode/QR at zones; unauthorized, left-premise, and
                tamper events appear below and in Security incidents.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setIsTamperModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-700 rounded-full hover:bg-purple-50 transition-colors"
            >
              <ShieldAlert className="w-5 h-5" />
              Report tamper
            </button>
            <button
              type="button"
              onClick={() => setIsScanModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              <QrCode className="w-5 h-5" />
              Scan tag
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Movements</p>
              <p className="text-2xl font-bold text-gray-900">
                {movements.length}
              </p>
            </div>
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Package className="w-6 h-6 text-[#0fa9a6]" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Alerts</p>
              <p className="text-2xl font-bold text-red-600">
                {movements.filter((m) => m.status === "Alert").length}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Unauthorized</p>
              <p className="text-2xl font-bold text-orange-600">
                {
                  movements.filter((m) => m.movementType === "Unauthorized")
                    .length
                }
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Authorized</p>
              <p className="text-2xl font-bold text-green-600">
                {
                  movements.filter((m) => m.movementType === "Authorized")
                    .length
                }
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search equipment or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
          <div className="min-w-[150px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
            >
              <option value="ALL">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Alert">Alert</option>
            </select>
          </div>
          <div className="min-w-[180px]">
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
            >
              <option value="ALL">All event types</option>
              <option value="UnauthorizedMovement">Unauthorized movement</option>
              <option value="AssetLeftPremise">Left premise</option>
              <option value="TamperDetected">Tamper</option>
              <option value="AuthorizedMovement">Normal</option>
            </select>
          </div>
        </div>
      </div>

      {/* Movements List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : movements.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <Package className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">
              No equipment movements found
            </p>
            <p className="text-sm">
              {searchQuery || selectedRegion !== "ALL Regions"
                ? "Try adjusting your search or filters"
                : "Start scanning equipment tags to track movements"}
            </p>
          </div>
        ) : (
          <div className="tableBlock w-full">
            <div className="card">
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={movements}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={20}
                  paginationPageSizeSelector={[10, 20, 50]}
                  paginationAutoPageSize={false}
                  getRowStyle={(params) => {
                    return {
                      backgroundColor:
                        params.data?.status === "Alert" ? "#fef2f2" : "white",
                    };
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scan Modal */}
      <Modal
        isOpen={isScanModalOpen}
        onClose={() => {
          setIsScanModalOpen(false);
          setScanTag("");
          setScanZone("");
          setScanLocation("");
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            Scan Equipment Tag
          </h2>
          <div className="px-6 pb-6">
            <p className="text-sm text-gray-600 mb-4">
              Enter RFID/Barcode/QR tag or Equipment ID/Serial Number to scan
              equipment
            </p>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Equipment Tag (RFID/Barcode/QR/ID){" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={scanTag}
                  onChange={(e) => setScanTag(e.target.value)}
                  placeholder="Scan or enter equipment tag, ID, or serial number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  autoFocus
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Zone <span className="text-red-500">*</span>
                </label>
                <select
                  value={scanZone}
                  onChange={(e) => setScanZone(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select zone</option>
                  {zones.map((zone) => (
                    <option key={zone.value} value={zone.value}>
                      {zone.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Location
                </label>
                <input
                  type="text"
                  value={scanLocation}
                  onChange={(e) => setScanLocation(e.target.value)}
                  placeholder="Enter location"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsScanModalOpen(false);
              setScanTag("");
              setScanZone("");
              setScanLocation("");
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleScanTag}
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Scan
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isTamperModalOpen}
        onClose={() => {
          setIsTamperModalOpen(false);
          setTamperTag("");
          setTamperAssetId("");
          setTamperZone("");
          setTamperLocation("");
          setTamperNotes("");
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg relative flex flex-col max-h-[85vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            Report tamper / tag anomaly
          </h2>
          <div className="px-6 pb-6 space-y-4">
            <p className="text-sm text-gray-600">
              Logs a <strong>Tamper detected</strong> security event and opens a
              matching Security incident. Provide a tag/ID that matches
              Equipment Management inventory.
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Tag / equipment ID / serial
              </label>
              <input
                type="text"
                value={tamperTag}
                onChange={(e) => setTamperTag(e.target.value)}
                placeholder="RFID, barcode, QR, equipment ID…"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Or equipment record ID (Mongo _id)
              </label>
              <input
                type="text"
                value={tamperAssetId}
                onChange={(e) => setTamperAssetId(e.target.value)}
                placeholder="Optional if tag above is set"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Zone</label>
              <select
                value={tamperZone}
                onChange={(e) => setTamperZone(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              >
                <option value="">Current / last known</option>
                {zones.map((zone) => (
                  <option key={zone.value} value={zone.value}>
                    {zone.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                type="text"
                value={tamperLocation}
                onChange={(e) => setTamperLocation(e.target.value)}
                placeholder="Where tamper was observed"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={tamperNotes}
                onChange={(e) => setTamperNotes(e.target.value)}
                rows={3}
                placeholder="Seal broken, reader mismatch, etc."
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
              />
            </div>
          </div>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsTamperModalOpen(false);
              setTamperTag("");
              setTamperAssetId("");
              setTamperZone("");
              setTamperLocation("");
              setTamperNotes("");
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReportTamper}
            className="px-4 py-2 bg-purple-700 text-white rounded-full hover:bg-purple-800 transition"
          >
            Log tamper event
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AssetMovementTracking;
